/**
 * The task row's check circle and assignee pill (DD-017), shared by TaskRow
 * and the task drawer's header so both read the same.
 */

import { CheckIcon, CloseIcon, UserIcon } from "@/components/icons";
import { cn } from "@/utils/cn";
import { assigneePillStyle } from "../taskAssigneeConfig";
import type { Task, TaskAssignee } from "../types";

interface TaskCheckButtonProps {
  task: Task;
  toggleable: boolean;
  onToggle: (task: Task) => void;
}

/** The one-tap check-off. It never opens the drawer: the click stops here. */
export function TaskCheckButton({ task, toggleable, onToggle }: TaskCheckButtonProps) {
  const done = task.status === "done";
  return (
    <button
      type="button"
      disabled={!toggleable}
      onClick={(event) => {
        event.stopPropagation();
        onToggle(task);
      }}
      aria-label={done ? "Reopen task" : "Complete task"}
      className={cn(
        "shrink-0 w-5 h-5 rounded-full border flex items-center justify-center transition-colors",
        done
          ? "bg-emerald-500 border-emerald-500 text-white"
          : "border-gray-300 text-transparent hover:border-emerald-400",
        toggleable ? "cursor-pointer" : "cursor-default opacity-60"
      )}
    >
      <CheckIcon className="w-3 h-3" />
    </button>
  );
}

/** An owner or tag pill. `owner` marks the task's owner with a person icon
 * in place of the dot, and says so on hover. `onRemove` adds a tag's remove
 * control (TD-103). */
export function TaskAssigneePill({
  assignee,
  owner = false,
  onRemove,
}: {
  assignee: TaskAssignee | null;
  owner?: boolean;
  onRemove?: () => void;
}) {
  const pill = assigneePillStyle(assignee);
  return (
    <span
      title={owner ? `Assegnato a ${pill.label}` : undefined}
      className={cn(
        "shrink-0 inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[11px] font-medium border",
        pill.bg,
        pill.text,
        pill.border
      )}
    >
      {owner ? (
        <UserIcon className="w-3 h-3" aria-hidden="true" />
      ) : (
        <span className={cn("w-1.5 h-1.5 rounded-full", pill.dot)} />
      )}
      {owner && <span className="sr-only">Assegnato a</span>}
      {pill.label}
      {onRemove && (
        <button
          type="button"
          onClick={onRemove}
          aria-label={`Rimuovi ${pill.label}`}
          className="-mr-1 rounded-full p-0.5 hover:bg-black/5 focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-blue-500"
        >
          <CloseIcon className="w-3 h-3" />
        </button>
      )}
    </span>
  );
}
